/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { Redirect } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { WaitlistEntryReason } from '../../../common/analytics/types';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { BENCHMARK_ROUTE_PATH, HOME_ROUTE_PATH } from '../../../routes';
import { forceBenchmarkWaitlistViewAtom } from '../../state';

// Both pages are split into their own chunks and only fetched once this
// route decides to render them.
const BenchmarkPage = lazy(() =>
	import(/* webpackChunkName: "@atlassian/twg-microsite_benchmark-page" */ '../benchmark').then(
		(module) => ({ default: module.BenchmarkPage }),
	),
);

const BenchmarkWaitlistPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite_benchmark-waitlist-page" */ '../benchmark-waitlist'
	).then((module) => ({ default: module.BenchmarkWaitlistPage })),
);

const LazyBenchmarkPage = (): JSX.Element => (
	<Suspense fallback={null}>
		<BenchmarkPage />
	</Suspense>
);

type LazyBenchmarkWaitlistPageProps = {
	isAuthed: boolean;
	entryReason: WaitlistEntryReason;
	/** The signed-in visitor's account email, which prefills the form. */
	email: string;
};

const LazyBenchmarkWaitlistPage = ({
	isAuthed,
	entryReason,
	email,
}: LazyBenchmarkWaitlistPageProps): JSX.Element => (
	<Suspense fallback={null}>
		<BenchmarkWaitlistPage
			authState={isAuthed ? 'loggedIn' : 'loggedOut'}
			entryReason={entryReason}
			defaultEmail={isAuthed ? email : ''}
		/>
	</Suspense>
);

/**
 * Route component for `/benchmark`.
 *
 * Signed-in users with the cloud benchmark get the benchmark. With public
 * benchmarking on, everyone else gets the waitlist. `isAuthed` is final here
 * because `UserProvider` only mounts the router once the profile fetch settles.
 * The cloud gate is checked first so its users are not exposed to the public
 * gate. The Debug Panel can force the waitlist view to preview it.
 */
export const BenchmarkRoute = (): JSX.Element => {
	const { isAuthed, identity } = useAtlassianUserContext();
	const forceWaitlistView = useAtomValue(forceBenchmarkWaitlistViewAtom);

	if (forceWaitlistView) {
		return (
			<LazyBenchmarkWaitlistPage
				isAuthed={isAuthed}
				entryReason="debugOverride"
				email={identity.email}
			/>
		);
	}

	if (fg('graphyte-twg-cloud-benchmark') && isAuthed) {
		return <LazyBenchmarkPage />;
	}

	if (fg('twg-microsite-team-eu-public-benchmarking')) {
		return (
			<LazyBenchmarkWaitlistPage
				isAuthed={isAuthed}
				entryReason={isAuthed ? 'publicLoggedInWithoutCloudBenchmark' : 'publicLoggedOut'}
				email={identity.email}
			/>
		);
	}

	return <Redirect to={HOME_ROUTE_PATH} />;
};

/**
 * Route component for a run, its review and its report. Mirrors
 * `BenchmarkRoute`, except that a visitor owed the waitlist is sent to
 * `/benchmark`, which shows it, since a run means nothing to them.
 */
export const BenchmarkRunRoute = (): JSX.Element => {
	const { isAuthed } = useAtlassianUserContext();

	if (fg('graphyte-twg-cloud-benchmark') && isAuthed) {
		return <LazyBenchmarkPage />;
	}

	if (fg('twg-microsite-team-eu-public-benchmarking')) {
		return <Redirect to={BENCHMARK_ROUTE_PATH} />;
	}

	return <Redirect to={HOME_ROUTE_PATH} />;
};
